import { Instance, destroy, detach, flow, types } from 'mobx-state-tree';

import { createMeet, getMeets } from '@common/api/meets';
import PeriodModel from '@common/models/PeriodModel';

import { IGroupModel } from '../Groups/Group';
import ReworkingMeet, { IReworkingMeetSnapshotOut } from './ReworkingMeet';
import { getDaySlotMap, getSlotMap } from './constants';

interface IReworkingFormData extends Partial<IReworkingMeetSnapshotOut> {
	group: IGroupModel;
	student_id: number;
	program_group_id: number;
	gradebook_id: number;
	meet_slot_id: number;
	program: number;
}

const ReworkingMeets = types
	.compose(
		PeriodModel,
		types.model({
			data: types.optional(types.array(ReworkingMeet), []),
			service_id: types.number,
			location_id: types.number,
			teacher_id: types.maybe(types.number),
			limit: 300,
			slotDuration: types.optional(types.number, 20),
		}),
	)
	.named('ReworkingMeets')
	.volatile(
		(): {
			reworkingFormData: IReworkingFormData | null;
		} => ({
			reworkingFormData: null,
		}),
	)
	.views((self) => ({
		get slotsInHour() {
			return Math.trunc(60 / self.slotDuration);
		},
	}))
	.views((self) => ({
		get daysMap() {
			const result = getSlotMap(self.slotsInHour);
			self.data.forEach((item) => {
				result[item.weekday][item.hourNumber][item.hourSlotNumber] = item;
			});
			return result;
		},
		get slotsMap() {
			const result = getDaySlotMap(self.slotsInHour);
			self.data.forEach((item) => {
				result[item.weekday][item.lessonSlotNumber] = item;
			});
			return result;
		},
	}))
	.actions((self) => ({
		_fetch: (query: any) =>
			getMeets({
				...query,
				location_id: self.location_id,
				period_start: self.period_start,
				period_end: self.period_end,
				teacher_id: self.teacher_id,
			}),
		addMeet: flow(function* (data: any) {
			self.reworkingFormData = data;
			const result = yield createMeet({
				location_id: self.location_id,
				service_id: self.service_id,
				teacher_id: self.teacher_id,
				...data,
			});
			self.data.push(result);
		}),
		removeMeet(meet: any) {
			detach(meet);
			destroy(meet);
		},
	}));

export default ReworkingMeets;

export interface IReworkingMeetsModel extends Instance<typeof ReworkingMeets> {}
